import { c as _c } from "react/compiler-runtime";
import type { ComboboxItem } from '@src/components/sco/ComboboxTagInput';
import { ScoTagComboboxInput } from '@src/components/sco/ScoTagComboboxInput';
import { SkillsComboboxInput } from '@src/components/sco/SkillsComboboxInput';

/**
 * Which taxonomy source powers a `field-type=tags` sco-question.
 *
 * - `industry` (and aliases) → ScoTag `investor_industry` (same catalog as company industry picks elsewhere)
 * - `skill(s)` → SkillsComboboxInput (scoTags skill type)
 * - other keys (incl. investor_vertical, etc.) → ScoTagComboboxInput with those type keys
 */
export type ScoQuestionTagsSource = 'skill' | 'sco-tag';

/**
 * Bank / model may emit short aliases (`industry`, `skills`). Normalize to real Tag.tagTypeKey values.
 * `industry` is investor_industry ScoTags — not the static scoIndustries reference list.
 */
export function normalizeScoQuestionTagTypeKeys(tagTypeKeys: string[]): string[] {
  const keys = tagTypeKeys.map(k => k.toLowerCase().trim()).filter(Boolean);
  if (keys.length === 0) {
    return ['investor_industry'];
  }
  return keys.map(k => {
    if (k === 'industry' || k === 'company_industry' || k === 'company-industry') {
      return 'investor_industry';
    }
    if (k === 'skills') {
      return 'skill';
    }
    return k;
  });
}
export function resolveScoQuestionTagsSource(tagTypeKeys: string[]): ScoQuestionTagsSource {
  const keys = normalizeScoQuestionTagTypeKeys(tagTypeKeys);
  if (keys.every(k => k === 'skill')) {
    return 'skill';
  }
  return 'sco-tag';
}
function titlesFromItems(items: ComboboxItem[] | undefined | null): string[] {
  return (items || []).map(i => i.title).filter(Boolean) as string[];
}
export type ScoQuestionTagsFieldProps = {
  tagTypeKeys: string[];
  selectedTitles: string[];
  onChange: (titles: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
};

/**
 * Query-backed tag/industry/skill picker for chat sco-question embeds.
 * Routes to the correct combobox so results actually load.
 */
export function ScoQuestionTagsField(props) {
  const $ = _c(25);
  let source;
  let t0;
  let tagTypeKeys;
  if ($[0] !== props.placeholder || $[1] !== props.tagTypeKeys) {
    tagTypeKeys = normalizeScoQuestionTagTypeKeys(props.tagTypeKeys);
    source = resolveScoQuestionTagsSource(props.tagTypeKeys);
    t0 = props.placeholder || placeholderFor(tagTypeKeys, source);
    $[0] = props.placeholder;
    $[1] = props.tagTypeKeys;
    $[2] = source;
    $[3] = t0;
    $[4] = tagTypeKeys;
  } else {
    source = $[2];
    t0 = $[3];
    tagTypeKeys = $[4];
  }
  let t1;
  if ($[5] !== props.disabled || $[6] !== t0) {
    const inputProps = {
      placeholder: t0,
      disabled: props.disabled,
      fontSize: "1em" as const
    };
    t1 = {
      allowMultipleSelections: true,
      inputProps
    };
    $[5] = props.disabled;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const comboboxTagInputProps = t1;
  if (source === "skill") {
    let t2;
    if ($[8] !== props.selectedTitles) {
      t2 = props.selectedTitles || [];
      $[8] = props.selectedTitles;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    let t3;
    if ($[10] !== t2) {
      t3 = t2.map(_temp);
      $[10] = t2;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    const defaultValue = t3;
    let t4;
    if ($[12] !== props) {
      t4 = items => props.onChange(titlesFromItems(items));
      $[12] = props;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t5;
    if ($[14] !== comboboxTagInputProps || $[15] !== defaultValue || $[16] !== t4) {
      t5 = <SkillsComboboxInput defaultValue={defaultValue} comboboxTagInputProps={comboboxTagInputProps} onChange={t4} />;
      $[14] = comboboxTagInputProps;
      $[15] = defaultValue;
      $[16] = t4;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    return t5;
  }
  let t2;
  if ($[18] !== props) {
    t2 = tags => props.onChange((tags || []).map(_temp2).filter(Boolean) as string[]);
    $[18] = props;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  let t3;
  if ($[20] !== comboboxTagInputProps || $[21] !== props.selectedTitles || $[22] !== t2 || $[23] !== tagTypeKeys) {
    t3 = <ScoTagComboboxInput defaultSelectedTagTitles={props.selectedTitles} tagTypeKeys={tagTypeKeys} comboboxTagInputProps={comboboxTagInputProps} onSelectedTagsChange={t2} />;
    $[20] = comboboxTagInputProps;
    $[21] = props.selectedTitles;
    $[22] = t2;
    $[23] = tagTypeKeys;
    $[24] = t3;
  } else {
    t3 = $[24];
  }
  return t3;
}
function _temp2(t) {
  return t.title;
}
function _temp(title) {
  return {
    key: title,
    title
  };
}
function placeholderFor(tagTypeKeys: string[], source: ScoQuestionTagsSource): string {
  if (source === 'skill') {
    return 'Search skills…';
  }
  if (tagTypeKeys.every(k => k === 'investor_industry')) {
    return 'Search industries…';
  }
  if (tagTypeKeys.some(k => k.startsWith('investor_'))) {
    return 'Search investor tags…';
  }
  return 'Search tags…';
}